/**
 * MessageView
 *
 * Full display for a single email message with headers, body, attachments,
 * and action buttons.
 */

import {
  Reply,
  ReplyAll,
  Forward,
  Paperclip,
  Download,
  MoreHorizontal,
  Star,
  Mail,
  FolderInput,
  Tag,
  Printer,
  ShieldAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { AIPhishingBanner } from './AIPhishingBanner';
import type { EmailMessageDetail, EmailAttachment, EmailLabelRef } from '@/lib/api';

interface MessageViewProps {
  message: EmailMessageDetail;
  accountId?: string;
  securityEnabled?: boolean;
  senderInContacts?: boolean;
  onReply: () => void;
  onReplyAll: () => void;
  onForward: () => void;
  onToggleStar?: () => void;
  onMarkUnread?: () => void;
  onDownloadAttachment?: (attachment: EmailAttachment) => void;
}

function formatFullDate(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function senderInitial(name: string | null, address: string): string {
  return (name || address).charAt(0).toUpperCase();
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1048576) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / 1048576).toFixed(1)}MB`;
}

export function MessageView({
  message,
  accountId,
  securityEnabled,
  senderInContacts = true,
  onReply,
  onReplyAll,
  onForward,
  onToggleStar,
  onMarkUnread,
  onDownloadAttachment,
}: MessageViewProps) {
  const toList = message.toAddresses?.map((a) => a.name || a.email).join(', ') || '';
  const ccList = message.ccAddresses?.map((a) => a.name || a.email).join(', ') || '';

  return (
    <div className="flex flex-col">
      {/* Phishing banner */}
      {securityEnabled && accountId && (
        <AIPhishingBanner
          accountId={accountId}
          messageId={message.id}
          senderInContacts={senderInContacts}
        />
      )}

      {/* Header */}
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          {/* Sender */}
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
              {senderInitial(message.fromName, message.fromAddress)}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">
                  {message.fromName || message.fromAddress}
                </span>
                {message.fromName && (
                  <span className="text-xs text-muted-foreground">
                    &lt;{message.fromAddress}&gt;
                  </span>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {toList && <span>To: {toList}</span>}
                {ccList && <span className="ml-2">Cc: {ccList}</span>}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatFullDate(message.sentAt || message.createdAt)}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {onToggleStar && (
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onToggleStar}>
                <Star
                  className={cn(
                    'h-4 w-4',
                    message.isStarred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground',
                  )}
                />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={onReply}
              title="Reply"
            >
              <Reply className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={onReplyAll}
              title="Reply All"
            >
              <ReplyAll className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={onForward}
              title="Forward"
            >
              <Forward className="h-4 w-4" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onMarkUnread && (
                  <DropdownMenuItem onClick={onMarkUnread}>
                    <Mail className="h-4 w-4 mr-2" /> Mark unread
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem disabled>
                  <FolderInput className="h-4 w-4 mr-2" /> Move to folder
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <Tag className="h-4 w-4 mr-2" /> Add label
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled>
                  <Printer className="h-4 w-4 mr-2" /> Print
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <ShieldAlert className="h-4 w-4 mr-2" /> Report phishing
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Labels */}
        {message.labels && message.labels.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {message.labels.map((label: EmailLabelRef) => (
              <Badge
                key={label.id}
                variant="outline"
                className="text-xs"
                style={{ borderColor: label.color, color: label.color }}
              >
                {label.name}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="px-4 py-2">
        {message.bodyHtml ? (
          <div
            className="prose prose-sm dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: message.bodyHtml }}
          />
        ) : (
          <pre className="whitespace-pre-wrap text-sm font-sans">{message.bodyText || ''}</pre>
        )}
      </div>

      {/* Attachments */}
      {message.attachments && message.attachments.length > 0 && (
        <div className="px-4 py-3 border-t border-border">
          <div className="text-xs font-medium text-muted-foreground mb-2">
            {message.attachments.length} attachment{message.attachments.length !== 1 ? 's' : ''}
          </div>
          <div className="flex flex-wrap gap-2">
            {message.attachments.map((att: EmailAttachment) => (
              <button
                key={att.id}
                className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2 text-sm hover:bg-muted/80 transition-colors"
                onClick={() => onDownloadAttachment?.(att)}
              >
                <Paperclip className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <div className="text-left min-w-0">
                  <div className="truncate max-w-[180px] font-medium text-xs">{att.filename}</div>
                  <div className="text-xs text-muted-foreground">
                    {formatFileSize(att.sizeBytes)}
                  </div>
                </div>
                <Download className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom reply bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-t border-border">
        <Button variant="outline" size="sm" onClick={onReply} className="gap-1.5">
          <Reply className="h-3.5 w-3.5" /> Reply
        </Button>
        <Button variant="outline" size="sm" onClick={onReplyAll} className="gap-1.5">
          <ReplyAll className="h-3.5 w-3.5" /> Reply All
        </Button>
        <Button variant="outline" size="sm" onClick={onForward} className="gap-1.5">
          <Forward className="h-3.5 w-3.5" /> Forward
        </Button>
      </div>
    </div>
  );
}
